import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import axios from 'axios';
import SmartMarkdown from '../../components/SmartMarkdown/SmartMarkdown';
import '../../components/SmartMarkdown/SmartMarkdown.css';
import { 
  MessageSquare, 
  Send, 
  Bot, 
  User, 
  Zap, 
  Settings, 
  ExternalLink,
  Headphones,
  TrendingUp,
  Users,
  ShoppingCart,
  Target,
  Lightbulb,
  Phone,
  Copy,
  Check,
  RefreshCw
} from 'lucide-react';

// Using shared markdown parser from sharedResponseUtils

const SalesAssist = () => {
  const { t } = useTranslation();
  const { getCurrentTenant } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [loadingSessionHistory, setLoadingSessionHistory] = useState(false);
  const [lineProfile, setLineProfile] = useState(null);
  const [lineProfileLoading, setLineProfileLoading] = useState(false);
  const [salesPrompts, setSalesPrompts] = useState([]);
  const [regeneratingPrompts, setRegeneratingPrompts] = useState(false);
  const [lineGroups, setLineGroups] = useState([]);
  const [lineGroupsLoading, setLineGroupsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Helper function to get tenant-specific collection name
  const getTenantCollectionName = () => {
    const tenant = getProtectedTenantInfo();
    if (!tenant || tenant.code === 'DEFAULT') {
      return 'LOADING_Sales'; // Temporary fallback while refreshing
    }
    return `${tenant.code}_Sales`;
  };

  // Default sales prompts (fallback if not configured)
  const getDefaultSalesPrompts = () => [
    {
      icon: ShoppingCart,
      text: "แนะนำผลิตภัณฑ์และโซลูชันที่เหมาะสมสำหรับลูกค้า",
      color: "text-blue-600"
    },
    {
      icon: Settings,
      text: "อธิบายเทคโนโลยีและฟีเจอร์ของบริการต่างๆ",
      color: "text-purple-600"
    },
    {
      icon: Target,
      text: "มีงานอีเวนต์หรือโปรโมชั่นอะไรบ้างในช่วงนี้",
      color: "text-green-600"
    },
    {
      icon: TrendingUp,
      text: "แนะนำกลยุทธ์การตลาดสำหรับผลิตภัณฑ์นี้",
      color: "text-orange-600"
    },
    {
      icon: Lightbulb,
      text: "ช่วยสร้างแคมเปญการตลาดให้หน่อย",
      color: "text-yellow-600"
    },
    {
      icon: Users,
      text: "เปรียบเทียบแพ็กเกจบริการแต่ละประเภท",
      color: "text-red-600"
    }
  ];

  // Load tenant-specific AI configuration and Line configuration
  const loadAiConfiguration = async () => {
    try {
      setConfigLoading(true);
      const tenant = getProtectedTenantInfo();
      if (!tenant || tenant.code === 'DEFAULT') {
        return;
      }
      
      // Load both AI config and Line config in parallel
      const [aiConfigResponse, lineConfigResponse] = await Promise.all([
        axios.get(`/api/ai-config/tenant/${tenant.code}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }),
        axios.get(`/api/line-config/tenant/${tenant.code}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }).catch(() => ({ data: null })) // Gracefully handle if line-config doesn't exist yet
      ]);

      if (aiConfigResponse?.data) {
        setAiConfig(aiConfigResponse.data);
        
        // Load suggested prompts from config or use defaults
        if (aiConfigResponse.data?.prompts?.salesAssist?.suggestedQuestions && 
            Array.isArray(aiConfigResponse.data.prompts.salesAssist.suggestedQuestions) &&
            aiConfigResponse.data.prompts.salesAssist.suggestedQuestions.length > 0) {
          // Map configured prompts with icons
          const configuredPrompts = aiConfigResponse.data.prompts.salesAssist.suggestedQuestions.map((prompt, index) => {
            const iconMap = [ShoppingCart, Settings, Target, TrendingUp, Lightbulb, Users];
            const colorMap = ["text-blue-600", "text-purple-600", "text-green-600", "text-orange-600", "text-yellow-600", "text-red-600"];
            return {
              icon: iconMap[index % iconMap.length],
              text: prompt.text || prompt,
              color: prompt.color || colorMap[index % colorMap.length]
            };
          });
          setSalesPrompts(configuredPrompts);
          console.log('✅ Loaded suggested questions from tenant config:', configuredPrompts.length);
        } else {
          setSalesPrompts(getDefaultSalesPrompts());
          console.log('✅ Using default sales prompts');
        }
      }
      
      // Load Line configuration from new line_config.json
      if (lineConfigResponse?.data?.channels?.SalesAssist) {
        const salesAssistConfig = lineConfigResponse.data.channels.SalesAssist;
        // Update aiConfig with Line config for backward compatibility in UI
        setAiConfig(prev => ({
          ...prev,
          lineChannels: {
            ...prev?.lineChannels,
            salesAssist: salesAssistConfig
          }
        }));
        
        // If Line is configured, fetch profile information
        if (salesAssistConfig?.enabled && salesAssistConfig?.userAccessToken) {
          loadLineProfile(salesAssistConfig.userAccessToken);
        }
      } else if (aiConfigResponse?.data?.lineChannels?.salesAssist) {
        // Fallback to old ai_config.json structure if line_config.json doesn't exist
        console.log('Line config not found, using fallback from ai_config.json');
        const salesAssistConfig = aiConfigResponse.data.lineChannels.salesAssist;
        if (salesAssistConfig?.enabled && salesAssistConfig?.userAccessToken) {
          loadLineProfile(salesAssistConfig.userAccessToken);
        }
      }
    } catch (error) {
      console.error('Failed to load AI configuration:', error);
      // Use defaults on error
      setSalesPrompts(getDefaultSalesPrompts());
    } finally {
      setConfigLoading(false);
    }
  };

  // Load Line profile information
  const loadLineProfile = async (accessToken) => {
    try {
      setLineProfileLoading(true);
      
      // Fetch profile from Line API
      const response = await axios.get('https://api.line.me/v2/profile', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.data) {
        setLineProfile(response.data);
        console.log('✅ Line profile loaded:', response.data);
      }
    } catch (error) {
      console.error('Failed to load Line profile:', error);
      // If we can't get profile, we'll still show the QR code
      setLineProfile(null);
    } finally {
      setLineProfileLoading(false);
    }
  };

  // Load chat sessions for SalesAssist
  const loadChatSessions = async () => {
    try {
      setSessionsLoading(true);
      const response = await axios.get('/api/ai/chat/sessions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          chatSource: 'sales_assist',
          agentType: 'sales_assist',
          limit: 10,
          sortBy: 'StartTime',
          sortOrder: 'DESC'
        }
      });

      if (response.data?.data?.sessions) {
        setChatSessions(response.data.data.sessions);
      }
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Load registered Line groups
  const loadLineGroups = async () => {
    try {
      setLineGroupsLoading(true);
      const tenant = getProtectedTenantInfo();
      if (!tenant || tenant.code === 'DEFAULT') {
        return;
      }

      const response = await axios.get(`/api/line-management/groups/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data?.data) {
        // Filter only registered or listening groups
        const registeredGroups = response.data.data.filter(
          group => group.registrationState === 'registered' || group.registrationState === 'listening'
        );
        setLineGroups(registeredGroups);
        console.log('✅ Loaded Line groups:', registeredGroups.length);
      }
    } catch (error) {
      console.error('Failed to load Line groups:', error);
    } finally {
      setLineGroupsLoading(false);
    }
  };

  // Load recent active session
  const loadRecentSession = async () => {
    try {
      const response = await axios.get('/api/ai/chat/sessions/recent/sales_assist', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          agentType: 'SalesAssist'
        }
      });

      if (response.data?.data) {
        setCurrentSessionId(response.data.data.SessionID);
        return response.data.data.SessionID;
      }
    } catch (error) {
      console.error('Failed to load recent session:', error);
    }
    return null;
  };

  // Load chat history for a specific session
  const loadSessionHistory = async (sessionId) => {
    try {
      console.log('📋 [SalesAssist] Loading session history for session:', sessionId);
      
      // Show loading state
      setIsLoading(true);
      setLoadingSessionHistory(true); // Set loading state for session history
      
      const response = await axios.get(`/api/ai/chat/sessions/${sessionId}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data?.data?.conversations) {
        const sessionMessages = response.data.data.conversations.map(conv => [
          { role: 'user', content: conv.userMessage },
          { role: 'assistant', content: conv.aiResponse, metadata: conv.metadata }
        ]).flat();
        
        console.log('✅ [SalesAssist] Session history loaded successfully:', {
          sessionId,
          messageCount: sessionMessages.length,
          conversationsCount: response.data.data.conversations.length
        });
        
        setMessages(sessionMessages);
        setCurrentSessionId(sessionId);
        
        // Close session history panel after loading
        setShowSessionHistory(false);
        
        // Show success feedback
        console.log('📋 [SalesAssist] Chat area updated with session history');
        
        // Show brief success message
        const successMessage = { 
          role: 'assistant', 
          content: `✅ โหลดประวัติการสนทนาสำเร็จ (${sessionMessages.length} ข้อความ)`,
          isSuccess: true,
          temporary: true
        };
        setMessages(prev => [successMessage, ...sessionMessages]);
        
        // Remove success message after 3 seconds
        setTimeout(() => {
          setMessages(sessionMessages);
        }, 3000);
      } else {
        console.warn('⚠️ [SalesAssist] No conversations found in session history:', {
          sessionId,
          responseData: response.data
        });
        
        // Set empty messages if no history found
        setMessages([]);
        setCurrentSessionId(sessionId);
        setShowSessionHistory(false);
      }
    } catch (error) {
      console.error('❌ [SalesAssist] Failed to load session history:', {
        sessionId,
        error: error.message,
        errorResponse: error.response?.data
      });
      
      // Show error message to user
      const errorMessage = { 
        role: 'assistant', 
        content: '⚠️ ไม่สามารถโหลดประวัติการสนทนาได้ กรุณาลองใหม่อีกครั้ง',
        isError: true
      };
      setMessages([errorMessage]);
      setCurrentSessionId(sessionId);
      setShowSessionHistory(false);
    } finally {
      setIsLoading(false);
      setLoadingSessionHistory(false); // Reset loading state for session history
    }
  };

  // Start a new session
  const startNewSession = () => {
    setMessages([]);
    setCurrentSessionId(null);
    setShowSessionHistory(false);
  };

  // End current session
  const endCurrentSession = async () => {
    if (!currentSessionId) return;

    try {
      await axios.post(`/api/ai/chat/sessions/${currentSessionId}/end`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setCurrentSessionId(null);
      loadChatSessions(); // Refresh sessions list
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  };

  // Get webhook URL on component mount and load AI config
  useEffect(() => {
    const baseUrl = window.location.origin;
    const tenant = getProtectedTenantInfo();
    if (tenant && tenant.code && tenant.code !== 'DEFAULT') {
      setWebhookUrl(`${baseUrl}/api/webhook/line/sales-assist/${tenant.code}`);
    } else {
      setWebhookUrl(`${baseUrl}/api/webhook/line/sales-assist/DEMO`); // Fallback
    }
    // Set default prompts initially
    setSalesPrompts(getDefaultSalesPrompts());
    loadAiConfiguration();
    loadChatSessions();
    loadLineGroups();
  }, []);

  // Load recent session when component mounts
  useEffect(() => {
    const initializeSession = async () => {
      const recentSessionId = await loadRecentSession();
      if (recentSessionId) {
        await loadSessionHistory(recentSessionId);
      }
    };
    
    initializeSession();
  }, []);

  // Watch for AI configuration changes and refresh Line profile if needed
  useEffect(() => {
    if (aiConfig?.lineChannels?.salesAssist?.enabled && 
        aiConfig?.lineChannels?.salesAssist?.userAccessToken &&
        aiConfig?.lineChannels?.salesAssist?.userAccessToken !== lineProfile?.accessToken) {
      loadLineProfile(aiConfig.lineChannels.salesAssist.userAccessToken);
    }
  }, [aiConfig?.lineChannels?.salesAssist?.userAccessToken]);

  // Scroll to bottom only when sending new messages (not on load)
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Copy webhook URL to clipboard
  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle sending messages
  const handleSend = async () => {
    if (input.trim() === '') return;

    console.log('🚀 [SalesAssist] Starting message send process...', {
      input: input.substring(0, 100) + (input.length > 100 ? '...' : ''),
      inputLength: input.length,
      currentSessionId,
      messagesCount: messages.length,
      timestamp: new Date().toISOString()
    });

    const userMessage = { role: 'user', content: input };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);
    
    // Scroll to bottom after user sends message
    setTimeout(scrollToBottom, 100);

    const requestStartTime = Date.now();

    try {
      console.log('📋 [SalesAssist] Step 1: Preparing request data...');
      const collectionName = getTenantCollectionName();
      console.log('📋 [SalesAssist] Collection name:', collectionName);
      
      console.log('📋 [SalesAssist] Query preparation:', {
        original: currentInput,
        messageLength: currentInput.length
      });
      
      // Prepare request with tenant-specific AI configuration
      const requestData = {
        message: currentInput,
        history: messages,
        collection: collectionName,
        sessionId: currentSessionId, // Include current session ID
        options: {
          agentType: 'sales_assist', // This tells the backend to use salesAssist config
          useSystemPrompt: true // Use the system prompt from tenant config
        }
      };

      // If we have loaded AI config, include temperature and max tokens
      if (aiConfig?.prompts?.salesAssist) {
        requestData.options.temperature = aiConfig.prompts.salesAssist.temperature;
        requestData.options.max_tokens = aiConfig.prompts.salesAssist.maxTokens;
        console.log('📋 [SalesAssist] AI config loaded:', {
          temperature: aiConfig.prompts.salesAssist.temperature,
          maxTokens: aiConfig.prompts.salesAssist.maxTokens,
          model: aiConfig.models?.salesAssist?.model
        });
      } else {
        console.log('⚠️ [SalesAssist] No AI config loaded, using defaults');
      }

      console.log('📋 [SalesAssist] Request data prepared:', {
        messageLength: requestData.message.length,
        historyLength: requestData.history.length,
        collection: requestData.collection,
        sessionId: requestData.sessionId,
        options: requestData.options
      });

      console.log('🚀 [SalesAssist] Step 2: Making API call to /api/ai/chat/sales-assist...');
      const apiCallStartTime = Date.now();
      
      const response = await axios.post('/api/ai/chat/sales-assist', requestData, {
        timeout: 60000, // 60 second timeout
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const apiCallEndTime = Date.now();
      const apiCallDuration = apiCallEndTime - apiCallStartTime;
      
      console.log('✅ [SalesAssist] API call completed successfully!', {
        status: response.status,
        statusText: response.statusText,
        apiCallDuration: `${apiCallDuration}ms`,
        responseDataKeys: Object.keys(response.data || {}),
        hasReply: !!response.data?.reply,
        replyLength: response.data?.reply?.length || 0,
        hasMetadata: !!response.data?.metadata,
        timestamp: new Date().toISOString()
      });

      // Update current session ID if returned from response
      if (response.data.metadata?.session_id && !currentSessionId) {
        console.log('📋 [SalesAssist] New session ID received:', response.data.metadata.session_id);
        setCurrentSessionId(response.data.metadata.session_id);
      }

      // Create AI message with enhanced metadata for RAG
      const aiMessage = { 
        role: 'assistant', 
        content: response.data.reply,
        metadata: response.data.metadata || {},
        ragSources: response.data.metadata?.rag_context?.sources || null
      };
      
      console.log('📋 [SalesAssist] Creating AI message:', {
        contentLength: aiMessage.content?.length || 0,
        hasMetadata: !!aiMessage.metadata,
        hasRagSources: !!aiMessage.ragSources,
        ragSourcesCount: aiMessage.ragSources?.length || 0
      });
      
      setMessages(prevMessages => [...prevMessages, aiMessage]);
      
      // Scroll to bottom after AI response
      setTimeout(scrollToBottom, 100);
      
      const totalDuration = Date.now() - requestStartTime;
      console.log('✅ [SalesAssist] Message processing completed successfully!', {
        totalDuration: `${totalDuration}ms`,
        apiCallDuration: `${apiCallDuration}ms`,
        processingDuration: `${totalDuration - apiCallDuration}ms`,
        finalMessageCount: messages.length + 1
      });
      
    } catch (error) {
      const errorTime = Date.now();
      const totalDuration = errorTime - requestStartTime;
      
      console.error('❌ [SalesAssist] Error during message processing:', {
        error: error.message,
        errorType: error.constructor.name,
        errorCode: error.code,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        errorStatusText: error.response?.statusText,
        totalDuration: `${totalDuration}ms`,
        timestamp: new Date().toISOString()
      });
      
      // Log detailed error information
      if (error.response) {
        console.error('❌ [SalesAssist] Server error details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('❌ [SalesAssist] Network error details:', {
          request: error.request,
          message: error.message
        });
      } else {
        console.error('❌ [SalesAssist] Other error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      
      let errorContent = 'ขออภัย ระบบ SalesAssist มีปัญหาในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง';
      
      // Check for specific error types
      if (error.response?.data?.code === 'AI_CONFIG_ERROR') {
        errorContent = '⚠️ ระบบ AI ยังไม่ได้ตั้งค่า API Key กรุณาติดต่อผู้ดูแลระบบเพื่อตั้งค่า API Key สำหรับ OpenAI, Gemini หรือ OpenRouter';
        console.log('⚠️ [SalesAssist] AI configuration error detected');
      } else if (error.response?.data?.details?.includes('API key')) {
        errorContent = '⚠️ ไม่พบ API Key สำหรับ AI Service กรุณาติดต่อผู้ดูแลระบบ';
        console.log('⚠️ [SalesAssist] API key error detected');
      } else if (error.response?.status === 500) {
        errorContent = '⚠️ เกิดข้อผิดพลาดในระบบ AI กรุณาลองใหม่อีกครั้ง';
        console.log('⚠️ [SalesAssist] Server 500 error detected');
      } else if (error.response?.status === 401) {
        errorContent = '⚠️ ไม่มีสิทธิ์เข้าถึง กรุณาเข้าสู่ระบบใหม่';
        console.log('⚠️ [SalesAssist] Authentication error detected');
      } else if (error.code === 'ECONNREFUSED' || error.code === 'NETWORK_ERROR') {
        errorContent = '⚠️ ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
        console.log('⚠️ [SalesAssist] Network connection error detected');
      } else if (error.code === 'ECONNABORTED') {
        errorContent = '⚠️ การเชื่อมต่อหมดเวลา กรุณาลองใหม่อีกครั้ง';
        console.log('⚠️ [SalesAssist] Connection timeout detected');
      }
      
      const errorMessage = { 
        role: 'assistant', 
        content: errorContent,
        isError: true
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
      
      // Scroll to bottom after error message
      setTimeout(scrollToBottom, 100);
    } finally {
      setIsLoading(false);
      console.log('🏁 [SalesAssist] Message send process finished', {
        isLoading: false,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle session click with loading state
  const handleSessionClick = async (sessionId) => {
    if (isLoading || loadingSessionHistory) {
      console.log('⚠️ [SalesAssist] Already loading, ignoring session click');
      return;
    }
    
    if (sessionId === currentSessionId) {
      console.log('📋 [SalesAssist] Session already active, no need to reload');
      return;
    }
    
    console.log('🖱️ [SalesAssist] Session clicked:', sessionId);
    await loadSessionHistory(sessionId);
  };

  // Regenerate suggested questions using AI
  const regenerateQuestions = async () => {
    try {
      setRegeneratingPrompts(true);
      const tenant = getProtectedTenantInfo();
      
      if (!tenant || tenant.code === 'DEFAULT') {
        console.error('Cannot regenerate questions: No valid tenant');
        return;
      }

      const collectionName = getTenantCollectionName();
      console.log('🤖 Regenerating suggested questions for tenant:', tenant.code);
      console.log('📊 Using collection:', collectionName);

      const response = await axios.post(
        `/api/ai/prompts/generate/${tenant.code}`,
        {
          collection: collectionName,
          agentType: 'SalesAssist'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data?.success && response.data?.data?.questions) {
        console.log('✅ Questions regenerated successfully:', response.data.data);
        
        // Map new questions with icons
        const iconMap = [ShoppingCart, Settings, Target, TrendingUp, Lightbulb, Users];
        const colorMap = ["text-blue-600", "text-purple-600", "text-green-600", "text-orange-600", "text-yellow-600", "text-red-600"];
        
        const newPrompts = response.data.data.questions.map((prompt, index) => ({
          icon: iconMap[index % iconMap.length],
          text: prompt.text || prompt,
          color: prompt.color || colorMap[index % colorMap.length]
        }));

        setSalesPrompts(newPrompts);
        
        // Show success message
        const successMsg = {
          role: 'assistant',
          content: `✅ สร้างคำถามใหม่สำเร็จ! ระบบวิเคราะห์ข้อมูล CRM ล่าสุดและสร้างคำถามที่เหมาะสมกับสถานการณ์ปัจจุบันแล้ว (${newPrompts.length} คำถาม)`,
          isSuccess: true,
          temporary: true
        };
        setMessages(prev => [...prev, successMsg]);

        // Remove success message after 5 seconds
        setTimeout(() => {
          setMessages(prev => prev.filter(msg => !msg.temporary));
        }, 5000);
      }

    } catch (error) {
      console.error('❌ Failed to regenerate questions:', error);
      
      // Show error message
      const errorMsg = {
        role: 'assistant',
        content: `⚠️ ไม่สามารถสร้างคำถามใหม่ได้: ${error.response?.data?.details || error.message}`,
        isError: true,
        temporary: true
      };
      setMessages(prev => [...prev, errorMsg]);

      // Remove error message after 5 seconds
      setTimeout(() => {
        setMessages(prev => prev.filter(msg => !msg.temporary));
      }, 5000);
    } finally {
      setRegeneratingPrompts(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden transition-colors duration-300">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-white bg-opacity-20 rounded-lg">
                <Zap className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">SalesAssist</h1>
                <p className="text-blue-100 mt-1">
                  ผู้ช่วยขายอัจฉริยะสำหรับทีมขาย - รองรับ RAG จากฐานข้อมูล Sales
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg">
                <Bot className="h-4 w-4" />
                <span className="text-sm">
                  AI Agent: {configLoading ? 'Loading...' : (
                    aiConfig?.models?.salesAssist?.service === 'openai' ? 'OpenAI' :
                    aiConfig?.models?.salesAssist?.service === 'gemini' ? 'Gemini' :
                    aiConfig?.models?.salesAssist?.service === 'openrouter' ? 'OpenRouter' :
                    aiConfig?.models?.salesAssist?.service === 'huggingface' ? 'Hugging Face' :
                    'OpenAI'
                  )}
                </span>
              </div>
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm">RAG: {getTenantCollectionName()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 h-[calc(100vh-140px)] overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-full">
          
          {/* Left Sidebar - Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Sales Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Lightbulb className="h-5 w-5 mr-2 text-yellow-500" />
                  คำถามที่นิยม
                </h3>
                <button
                  onClick={regenerateQuestions}
                  disabled={regeneratingPrompts || configLoading}
                  className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="สร้างคำถามใหม่ด้วย AI"
                >
                  <RefreshCw className={`h-4 w-4 ${regeneratingPrompts ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {regeneratingPrompts && (
                <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                      กำลังวิเคราะห์ข้อมูล CRM และสร้างคำถามใหม่...
                    </span>
                  </div>
                </div>
              )}
              {aiConfig?.prompts?.salesAssist?.questionsAutoGenerated && aiConfig?.prompts?.salesAssist?.questionsLastUpdated && (
                <div className="mb-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-2">
                  <p className="text-xs text-green-700 dark:text-green-300">
                    🤖 คำถามสร้างโดย AI • อัปเดตล่าสุด: {new Date(aiConfig.prompts.salesAssist.questionsLastUpdated).toLocaleString('th-TH')}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {salesPrompts.map((prompt, index) => {
                  const Icon = prompt.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => setInput(prompt.text)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
                    >
                      <div className="flex items-start space-x-3">
                        <Icon className={`h-4 w-4 mt-0.5 ${prompt.color} group-hover:scale-110 transition-transform`} />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-blue-700 dark:group-hover:text-blue-400 leading-relaxed">
                          {prompt.text}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Chat Session Management */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors duration-300">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-purple-500" />
                Chat Sessions
              </h3>
            
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Current Session</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    currentSessionId 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {currentSessionId ? 'Active' : 'None'}
                  </span>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={startNewSession}
                    className="flex-1 px-3 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors"
                  >
                    New Session
                  </button>
                  {currentSessionId && (
                    <button
                      onClick={endCurrentSession}
                      className="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                    >
                      End
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setShowSessionHistory(!showSessionHistory)}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-between"
                >
                  <span>Recent Sessions</span>
                  <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                    {chatSessions.length}
                  </span>
                </button>

                {showSessionHistory && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {sessionsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">Loading...</span>
                      </div>
                    ) : chatSessions.length > 0 ? (
                      chatSessions.map((session) => (
                        <div
                          key={session.SessionID}
                          className={`p-3 rounded-md border cursor-pointer transition-colors ${
                            session.SessionID === currentSessionId
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                              : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                          } ${isLoading || loadingSessionHistory ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => handleSessionClick(session.SessionID)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center">
                              {session.SessionID === currentSessionId && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                              )}
                              {session.SessionName || `Session ${session.SessionID}`}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              session.Status === 'active' 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {session.Status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(session.StartTime).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {session.MessageCount} messages • {session.TotalTokens} tokens
                          </div>
                          {session.SessionID === currentSessionId && (
                            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
                              ✓ Currently active
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                        No sessions found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Chat Interface - 50% width */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-full max-h-[calc(100vh-200px)] flex flex-col transition-colors duration-300">
              
                            {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900 dark:text-gray-100">SalesAssist</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {configLoading ? 'Loading configuration...' : (
                        `${aiConfig?.models?.salesAssist?.service === 'openai' ? 'OpenAI' : 
                          aiConfig?.models?.salesAssist?.service === 'gemini' ? 'Gemini' : 
                          aiConfig?.models?.salesAssist?.service === 'openrouter' ? 'OpenRouter' : 
                          aiConfig?.models?.salesAssist?.service === 'huggingface' ? 'Hugging Face' : 
                          'OpenAI'} • RAG: ${getTenantCollectionName()}`
                      )}
                    </p>
                    {currentSessionId && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                        Session #{currentSessionId} • {messages.length} messages
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {configLoading ? (
                    <div className="flex items-center space-x-1 bg-yellow-100 dark:bg-yellow-900/30 px-3 py-1 rounded-full">
                      <div className="animate-spin h-3 w-3 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                      <span className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">Loading Config</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1 bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs text-green-700 dark:text-green-300 font-medium">
                        {aiConfig?.models?.salesAssist?.enabled ? 'SalesAssist Active' : 'SalesAssist Disabled'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingSessionHistory ? (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 transition-colors duration-300 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center">
                          <Bot className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          <span className="text-sm text-gray-700 dark:text-white font-medium">
                            กำลังโหลดประวัติการสนทนา...
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full mx-auto mb-4">
                      <Headphones className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      ยินดีต้อนรับสู่ SalesAssist
                    </h3>
                    <p className="text-gray-600 dark:text-gray-100 mb-4 max-w-md mx-auto font-medium">
                      ผู้ช่วยขายอัจฉริยะที่พร้อมช่วยในงานขาย การตลาด และการดูแลลูกค้า
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      เริ่มต้นด้วยการถามคำถาม หรือเลือกจากคำถามที่นิยมด้านซ้าย
                    </p>
                  </div>
                ) : (
                  messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-3xl ${
                        message.role === 'user' 
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-white border border-blue-200 dark:border-blue-700' 
                          : message.isError 
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700' 
                            : message.isSuccess
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600'
                      } rounded-lg p-4 transition-colors duration-300`}>
                        <div className="flex items-start space-x-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            message.role === 'user' 
                              ? 'bg-blue-500' 
                              : message.isError 
                                ? 'bg-red-500' 
                                : message.isSuccess
                                  ? 'bg-green-500'
                                  : 'bg-gradient-to-r from-teal-500 to-cyan-500'
                          }`}>
                            {message.role === 'user' ? (
                              <User className="h-4 w-4 text-white" />
                            ) : message.isError ? (
                              <div className="h-4 w-4 text-white font-bold">⚠️</div>
                            ) : message.isSuccess ? (
                              <div className="h-4 w-4 text-white font-bold">✅</div>
                            ) : (
                              <Bot className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <SmartMarkdown 
                              content={message.content}
                              className="prose prose-sm max-w-none dark:prose-invert"
                              enableCollapsibleLists={true}
                              minItemsForCollapse={2}
                              attachments={message.metadata?.attachments || []}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 transition-colors duration-300 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center">
                          <Bot className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          <span className="text-sm text-gray-700 dark:text-white font-medium">
                            กำลังคิดคำตอบ...
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="ถามเกี่ยวกับการขาย การตลาด หรือข้อมูลลูกค้า..."
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSend}
                    disabled={isLoading || input.trim() === ''}
                    className="px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2"
                  >
                    <Send className="h-4 w-4" />
                    <span>ส่ง</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                  SalesAssist ใช้ข้อมูลจาก {getTenantCollectionName()} Collection เพื่อตอบคำถามที่แม่นยำและเป็นประโยชน์
                </p>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Line Integration & Groups */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Line OA Integration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <ExternalLink className="h-5 w-5 mr-2 text-green-500" />
                  Line OA Integration
                </h3>
                <button
                  onClick={loadAiConfiguration}
                  disabled={configLoading}
                  className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh Line Configuration"
                >
                  <RefreshCw className={`h-4 w-4 ${configLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="space-y-4">
                {aiConfig?.lineChannels?.salesAssist?.enabled && 
                 aiConfig?.lineChannels?.salesAssist?.channelId && 
                 aiConfig?.lineChannels?.salesAssist?.channelSecret && 
                 aiConfig?.lineChannels?.salesAssist?.accessToken ? (
                  // Line is configured - show QR code and webhook info
                  <>
                    {/* Line Channel QR Code */}
                    {aiConfig?.lineChannels?.salesAssist?.qrCodeUrl && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
                        <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-3 flex items-center">
                          <Target className="h-4 w-4 mr-2" />
                          Line Channel QR Code
                        </h4>
                        <div className="text-center">
                          <img
                            src={aiConfig.lineChannels.salesAssist.qrCodeUrl}
                            alt="Line QR Code"
                            className="w-32 h-32 mx-auto object-contain border-2 border-green-200 dark:border-green-600 rounded-lg"
                          />
                          <p className="text-xs text-green-700 dark:text-green-300 font-medium mt-2">
                            สแกน QR Code เพื่อเพิ่ม Line Official Account
                          </p>
                          {aiConfig?.lineChannels?.salesAssist?.lineId && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              Line ID: {aiConfig.lineChannels.salesAssist.lineId}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Line Profile Information - Only show when profile is available */}
                    {lineProfile && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 flex items-center">
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Line Official Account Profile
                          </h4>
                          <button
                            onClick={() => loadLineProfile(aiConfig.lineChannels.salesAssist.userAccessToken)}
                            disabled={lineProfileLoading}
                            className="p-1 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                            title="Refresh Profile"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex items-center space-x-4">
                          {/* Profile Picture */}
                          {lineProfile.pictureUrl && (
                            <div className="flex-shrink-0">
                              <img
                                src={lineProfile.pictureUrl}
                                alt="Line Profile"
                                className="w-16 h-16 rounded-full border-2 border-blue-300 dark:border-blue-600 object-cover"
                              />
                            </div>
                          )}
                          
                          {/* Profile Details */}
                          <div className="flex-1">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                  {lineProfile.displayName || 'Unknown Name'}
                                </span>
                                {lineProfile.statusMessage && (
                                  <span className="text-xs text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded-full">
                                    {lineProfile.statusMessage}
                                  </span>
                                )}
                              </div>
                              
                              {lineProfile.userId && (
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                  User ID: {lineProfile.userId}
                                </p>
                              )}
                              
                              {lineProfile.language && (
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                  Language: {lineProfile.language}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Connection Status */}
                        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-600">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-xs text-green-700 dark:text-green-300 font-medium">
                              Line OA Connected Successfully
                            </span>
                          </div>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            สามารถรับข้อความจากลูกค้าผ่าน Line ได้แล้ว
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Loading Line Profile - Only show when loading */}
                    {lineProfileLoading && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          <span className="text-sm text-blue-700 dark:text-blue-300">
                            กำลังโหลดข้อมูล Line Profile...
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // Line is not configured - show message to contact admin
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-700">
                    <div className="text-center">
                      <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-800 rounded-full mx-auto mb-4">
                        <Settings className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                        Line OA ยังไม่ได้ตั้งค่า
                      </h4>
                      <p className="text-xs text-yellow-800 dark:text-yellow-300 mb-4">
                        เพื่อใช้งาน Line Official Account กับ SalesAssist 
                        กรุณาติดต่อผู้ดูแลระบบเพื่อตั้งค่า Line Channel
                      </p>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded border border-yellow-300 dark:border-yellow-600">
                        <p className="text-xs text-gray-700 dark:text-gray-300 font-medium mb-2">
                          สิ่งที่ต้องตั้งค่า:
                        </p>
                        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 text-left">
                          <li>• Line Channel ID</li>
                          <li>• Line Channel Secret</li>
                          <li>• Line Access Token</li>
                          <li>• Line ID (@username)</li>
                          <li>• QR Code สำหรับเพิ่ม Line OA</li>
                        </ul>
                      </div>
                      <div className="mt-4">
                        <button
                          onClick={() => window.location.href = '/line-management'}
                          className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors"
                        >
                          ไปที่ Line Configuration
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Registered Line Groups */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-green-500" />
                  Line Groups
                </h3>
                <button
                  onClick={loadLineGroups}
                  disabled={lineGroupsLoading}
                  className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh Line Groups"
                >
                  <RefreshCw className={`h-4 w-4 ${lineGroupsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              {lineGroupsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading groups...</span>
                </div>
              ) : lineGroups.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {lineGroups.map((group) => (
                    <div
                      key={group.id}
                      className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {group.groupName || 'Unnamed Group'}
                            </h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              group.registrationState === 'listening' 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                              {group.registrationState === 'listening' ? 'Active' : 'Registered'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {group.noteCount || 0} conversation logs
                          </p>
                          {group.latestNoteDate && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Last: {new Date(group.latestNoteDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <a
                        href={`/notes?search=group-id:${group.groupId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors mt-2"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        View Conversation Logs
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mx-auto mb-3">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    No Line groups registered yet
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Add this SalesAssist bot to Line groups to start logging conversations
                  </p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesAssist;